import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { authAPI } from '../utils/api';
import config from '../config';
import { debugLog, debugWarn } from '../utils/debug';

const AuthContext = createContext();
const AUTH_EXPIRED_EVENT = 'fashionmate:auth-expired';
const RETURN_TO_KEY = 'fashionmate_return_to';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authNotice, setAuthNotice] = useState(null);
  const [gsiScriptLoaded, setGsiScriptLoaded] = useState(false);
  const gsiInitializedRef = useRef(false);

  const clearAuthState = useCallback((message = null, returnTo = null) => {
    localStorage.removeItem('auth_token');
    authAPI.setAuthToken(null);
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    }
    setUser(null);
    setAuthNotice(message);
    if (message) setError(message);
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const hadToken = Boolean(localStorage.getItem('auth_token'));
    try {
      setLoading(true);
      const response = await authAPI.getAuthStatus();
      const payload = response.data?.data || response.data || {};
      if (payload.authenticated && payload.user) {
        setUser(payload.user);
        setError(null);
        setAuthNotice(null);
      } else {
        clearAuthState(hadToken ? 'Your session expired. Sign in again.' : null);
      }
    } catch (err) {
      if (err.isAuthError || err.response?.status === 401) {
        clearAuthState('Your session expired. Sign in again.');
      } else {
        setError('FashionMate could not confirm your session. Check your connection and try again.');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuthState]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    const handleExpired = (event) => {
      const returnTo = event.detail?.returnTo || `${window.location.pathname}${window.location.search}`;
      clearAuthState('Your session expired. Sign in again.', returnTo);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
  }, [clearAuthState]);

  useEffect(() => {
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      if (window.google) setGsiScriptLoaded(true);
      else existing.addEventListener('load', () => setGsiScriptLoaded(true), { once: true });
      return undefined;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => setGsiScriptLoaded(true);
    script.onerror = () => setError('Failed to load Google authentication services.');
    document.body.appendChild(script);
    return undefined;
  }, []);

  const handleGoogleCredentialResponse = useCallback(async (response) => {
    try {
      setLoading(true);
      setError(null);
      const authResponse = await authAPI.loginWithGoogle(response.credential);
      if (authResponse.authenticated && authResponse.user) {
        setUser(authResponse.user);
        setAuthNotice(null);
        return true;
      }
      setError('Failed to authenticate with Google.');
      return false;
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to authenticate with Google.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const initializeGoogleSignIn = useCallback((buttonId) => {
    if (!gsiScriptLoaded || !window.google) return;
    const button = document.getElementById(buttonId);
    if (!button) return;
    if (!config.GOOGLE_CLIENT_ID) {
      setError('Authentication configuration error.');
      return;
    }
    try {
      if (!gsiInitializedRef.current) {
        window.google.accounts.id.initialize({
          client_id: config.GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
        });
        gsiInitializedRef.current = true;
      }
      button.replaceChildren();
      const measuredWidth = Math.floor(button.getBoundingClientRect().width || 320);
      window.google.accounts.id.renderButton(button, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: Math.max(220, Math.min(400, measuredWidth)),
      });
    } catch (err) {
      setError('Failed to initialize authentication.');
    }
  }, [gsiScriptLoaded, handleGoogleCredentialResponse]);

  const login = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await authAPI.login();
      return true;
    } catch (err) {
      setError('Failed to initiate login.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const processLoginCallback = useCallback(async (token) => {
    try {
      localStorage.setItem('auth_token', token);
      authAPI.setAuthToken(token);
      const response = await authAPI.getAuthStatus();
      const payload = response.data?.data || response.data || {};
      if (payload.authenticated && payload.user) {
        setUser(payload.user);
        setError(null);
        setAuthNotice(null);
        return true;
      }
      clearAuthState('Authentication failed: invalid session.');
      return false;
    } catch (err) {
      clearAuthState('Authentication failed: could not validate the session.');
      return false;
    }
  }, [clearAuthState]);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      if (window.google?.accounts) {
        try {
          window.google.accounts.id.disableAutoSelect();
        } catch (err) {
          debugWarn('Could not disable Google auto select.');
        }
      }
      await authAPI.logout();
      clearAuthState();
      return true;
    } catch (err) {
      clearAuthState();
      setError('Signed out locally, but the server could not be reached.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [clearAuthState]);

  const handleAuthError = useCallback((returnTo = null) => {
    debugLog('Clearing an expired FashionMate session.');
    clearAuthState('Your session expired. Sign in again.', returnTo);
  }, [clearAuthState]);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      authNotice,
      isAuthenticated: Boolean(user),
      login,
      logout,
      processLoginCallback,
      checkAuthStatus,
      handleAuthError,
      initializeGoogleSignIn,
      gsiScriptLoaded,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthContext;
